import { useState, type ReactNode } from "react";
import {
  addShoppingItems,
  readdShoppingItems,
  selectAllShoppingItems,
  selectActiveListId,
  classifyShoppingImport,
  useAppDispatch,
  useAppSelector,
  useT,
  type ConsolidatedItem,
  type ShoppingImportClassification,
} from "@reciperoll/core";
import { useToast } from "../components/Toast";
import { ShoppingImportChoiceModal } from "../components/ShoppingImportChoiceModal";

// Adds consolidated ingredients to the shopping list, but never silently: if any
// already exist it asks (Add new / Re-add all / Cancel) rather than guessing.
// Returns `importItems` to kick it off and `choiceModal` to render.
export function useShoppingListImport() {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const t = useT();
  const liveItems = useAppSelector(selectAllShoppingItems);
  const listId = useAppSelector(selectActiveListId); // add to the active list
  const [pending, setPending] = useState<{
    items: ConsolidatedItem[];
    cls: ShoppingImportClassification;
    onDone?: () => void;
  } | null>(null);

  const importItems = (items: ConsolidatedItem[], onDone?: () => void) => {
    if (items.length === 0) {
      toast.show(t("recipe.noIngredientsToAdd"));
      return;
    }
    if (!listId) return; // no primary list yet (pre-bootstrap) — nothing to add to
    const cls = classifyShoppingImport(items, liveItems);
    // Nothing overlaps → just add.
    if (cls.presentActive.length + cls.presentChecked.length === 0) {
      dispatch(addShoppingItems({ items, listId }));
      toast.show(t("shopping.addedItems", { count: items.length }));
      onDone?.();
      return;
    }
    setPending({ items, cls, onDone });
  };

  const addNew = () => {
    if (!pending || !listId) return;
    const n = pending.cls.newItems;
    dispatch(addShoppingItems({ items: n, listId }));
    toast.show(
      n.length ? t("shopping.addedNewItems", { count: n.length }) : t("shopping.nothingNewToAdd"),
    );
    pending.onDone?.();
    setPending(null);
  };

  const reAddAll = () => {
    if (!pending || !listId) return;
    dispatch(readdShoppingItems({ items: pending.items, listId }));
    const checked = pending.cls.presentChecked.length;
    toast.show(
      t("shopping.reAddedItems", { count: pending.items.length }) +
        (checked ? t("shopping.untickedSuffix", { count: checked }) : ""),
    );
    pending.onDone?.();
    setPending(null);
  };

  const choiceModal: ReactNode = pending ? (
    <ShoppingImportChoiceModal
      total={pending.items.length}
      newCount={pending.cls.newItems.length}
      checkedCount={pending.cls.presentChecked.length}
      onAddNew={addNew}
      onReAddAll={reAddAll}
      onCancel={() => setPending(null)}
    />
  ) : null;

  return { importItems, choiceModal };
}
